import { z } from 'zod';
import { baseComponentSchema, buttonSchema } from '../component-base.schemas';

// Head Content component schema
export const headContentComponentSchema = baseComponentSchema.extend({
  type: z.literal('head-content'),
  data: z.record(z.any()),
});

// Body End component schema
export const bodyEndComponentSchema = baseComponentSchema.extend({
  type: z.literal('body-end'),
  data: z.record(z.any()),
});

// Title Subtitle button schema
export const titleSubtitleButtonSchema = z
  .object({
    enabled: z.boolean().default(false),
    text: z.string().min(1).max(100).optional(),
    url: z.string().min(1).optional(),
    style: z.enum(['primary', 'secondary', 'outline', 'ghost']).default('primary'),
    target: z.enum(['_self', '_blank']).default('_self'),
  })
  .optional();

// Title Subtitle custom colors schema
export const titleSubtitleCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    title: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    subtitle: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    buttonBg: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    buttonText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
  })
  .optional();

// Title Subtitle component schema
export const titleSubtitleComponentSchema = baseComponentSchema.extend({
  type: z.literal('title-subtitle'),
  data: z.object({
    title: z.string().max(300, 'Title must be less than 300 characters').optional(),
    subtitle: z.string().max(500, 'Subtitle must be less than 500 characters').optional(),
    backgroundColor: z.string().nullable().optional(),
    titleTag: z.enum(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']).default('h2'),
    titleAlignment: z.enum(['left', 'center', 'right']).default('center'),
    titleColor: z.string().nullable().optional(),
    titleFontSize: z.string().optional(),
    titleFontWeight: z.enum(['normal', 'bold', 'light']).default('bold'),
    subtitleTag: z.enum(['p', 'h3', 'h4', 'h5', 'h6', 'span']).default('p'),
    subtitleAlignment: z.enum(['left', 'center', 'right']).default('center'),
    subtitleColor: z.string().nullable().optional(),
    subtitleFontSize: z.string().optional(),
    subtitleFontWeight: z.enum(['normal', 'bold', 'light']).default('normal'),
    spacing: z.enum(['tight', 'normal', 'loose']).default('normal'),
    button: titleSubtitleButtonSchema,
    customColors: titleSubtitleCustomColorsSchema,
  }),
});

// Contact Info custom colors schema
export const contactInfoCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    title: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    text: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    accent: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonBg: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
  })
  .optional();

// Contact Info component schema
export const contactInfoComponentSchema = baseComponentSchema.extend({
  type: z.literal('contact-info'),
  data: z.object({
    title: z.string().max(200, 'Title must be less than 200 characters').optional(),
    subtitle: z.string().max(500, 'Subtitle must be less than 500 characters').optional(),
    description: z.string().max(1000, 'Description must be less than 1000 characters').optional(),
    businessName: z.string().max(200, 'Business name must be less than 200 characters').optional(),
    address: z.string().max(500, 'Address must be less than 500 characters').optional(),
    phone: z.string().max(50, 'Phone must be less than 50 characters').optional(),
    email: z
      .string()
      .email('Must be a valid email')
      .max(100, 'Email must be less than 100 characters')
      .optional(),
    hours: z.string().max(500, 'Hours must be less than 500 characters').optional(),
    mapEmbedUrl: z
      .string()
      .url('Must be a valid URL')
      .max(2000, 'Map URL must be less than 2000 characters')
      .optional(),
    layout: z.enum(['map-left', 'map-right']).default('map-right'),
    showMap: z.boolean().default(true),
    backgroundColor: z.string().nullable().optional(),
    textColor: z.string().nullable().optional(),
    accentColor: z.string().nullable().optional(),
    // NEW: Component-specific custom colors that override theme colors
    customColors: contactInfoCustomColorsSchema,
    // Optional call-to-action buttons
    buttons: z.array(buttonSchema).optional(),
  }),
});

// HTML Embed component schema
export const htmlEmbedComponentSchema = baseComponentSchema.extend({
  type: z.literal('html-embed'),
  data: z.record(z.any()),
});

// Content Area component - Special placeholder for page layouts
// Marks where page-specific content will be rendered within a layout
export const contentAreaDataSchema = z.object({
  label: z.string().optional().default('Page Content'),
  minHeight: z.number().optional().default(200),
  backgroundColor: z.string().nullable().optional(),
  padding: z
    .object({
      top: z.number().default(0),
      bottom: z.number().default(0),
      left: z.number().default(0),
      right: z.number().default(0),
    })
    .optional(),
  maxWidth: z.enum(['full', 'container', 'narrow']).optional().default('container'),
  centered: z.boolean().optional().default(true),
});

export const contentAreaComponentSchema = baseComponentSchema.extend({
  type: z.literal('content-area'),
  data: contentAreaDataSchema,
});

// Type exports
export type TitleSubtitleComponent = z.infer<typeof titleSubtitleComponentSchema>;
export type ContactInfoComponent = z.infer<typeof contactInfoComponentSchema>;
export type HeadContentComponent = z.infer<typeof headContentComponentSchema>;
export type BodyEndComponent = z.infer<typeof bodyEndComponentSchema>;
export type HtmlEmbedComponent = z.infer<typeof htmlEmbedComponentSchema>;
export type ContentAreaData = z.infer<typeof contentAreaDataSchema>;
export type ContentAreaComponent = z.infer<typeof contentAreaComponentSchema>;
